import Information16 from '@carbon/icons-react/es/information/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { ICellRendererParams } from 'ag-grid-community';
import { Space, Tooltip, Typography } from 'antd';
import { parsePhoneNumber } from 'libphonenumber-js';
import moment from 'moment';
import { empty } from 'ramda';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  FormChoiceOption,
  FormQuestionMetaValue,
  FormQuestionType,
} from 'api/form';
import { MemberType } from 'api/models';
import { DueDate } from 'components/atoms/due-date';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { UserTag } from 'components/atoms/select';
import { Tag } from 'components/atoms/tag';
import styles from 'components/molecules/forms/form-fields/rte.module.css';
import { PlaceholderAvatar } from 'components/molecules/placeholder-avatar';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { AttachmentRenderer } from '../attachment';
import { StarRatingRenderer } from '../star-rating';

type CellRendererParamtypes = {
  questionType: FormQuestionType;
  questionOptions: FormChoiceOption[];
  questionMeta: {
    type: FormQuestionMetaValue;
    currencySymbol?: string;
    countryCode?: number;
    numberOfStar?: number;
  };
};

const TagMultiChoiceWrapper = styled.div`
  display: flex;
  gap: var(--spacing-02);
`;

const HeaderWrapper = styled.div`
  color: var(--grid-header-color);
  font-weight: var(--font-weight-bold);
  display: flex;
  gap: var(--spacing-02);
`;

const HeaderTitleIcon = styled.div`
  color: var(--gray-40);
`;

type FormCellRendererTypes = ICellRendererParams & CellRendererParamtypes;

const NoValue = styled.span.attrs({
  children: '-',
})`
  color: var(--coolest-gray-80);
`;

export const emptyState = <NoValue />;

export const FormCellRenderer = (params: FormCellRendererTypes) => {
  const {
    value,
    questionType,
    questionOptions = [],
    questionMeta,
    node: { group = false },
  } = params;

  // this is for hiding the empty value cell in group row
  if (group) {
    return null;
  }

  switch (questionType) {
    case FormQuestionType.Phone: {
      return value ? parsePhoneNumber(value).formatInternational() : emptyState;
    }

    case FormQuestionType.Email: {
      return value ?? empty;
    }

    case FormQuestionType.StarRating: {
      const { numberOfStar: maxValue } = questionMeta;

      if (value && maxValue) {
        return <StarRatingRenderer value={value} maxValue={Number(maxValue)} />;
      }

      return emptyState;
    }

    case FormQuestionType.Currency: {
      const { type, currencySymbol } = questionMeta;

      if (value && type === FormQuestionMetaValue.Currency) {
        return `${currencySymbol}${value}`;
      }

      return emptyState;
    }

    case FormQuestionType.TextLong: {
      return (
        <Typography.Text ellipsis title={value || ''}>
          {value ?? emptyState}
        </Typography.Text>
      );
    }

    case FormQuestionType.ChoiceRadio:
    case FormQuestionType.ChoiceDropdown: {
      const selectedChoice = questionOptions.find(
        (option) => option.optionId === value,
      );

      if (value && selectedChoice) {
        return (
          <Tag color={getRandomColor(value)}>{selectedChoice.optionLabel}</Tag>
        );
      }

      return emptyState;
    }

    case FormQuestionType.ChoiceCheckbox:
    case FormQuestionType.ChoiceDropdownMultiselect:
    case FormQuestionType.MatrixRow: {
      const selectedOptions = questionOptions.filter((option) =>
        (value ?? []).includes(option.optionId),
      );

      if (selectedOptions.length) {
        return (
          <TagMultiChoiceWrapper>
            {selectedOptions.map((selectedOption) => (
              <Tag color={getRandomColor(selectedOption.optionId)}>
                {selectedOption.optionLabel}
              </Tag>
            ))}
          </TagMultiChoiceWrapper>
        );
      }

      return emptyState;
    }

    case FormQuestionType.Date: {
      const isValidDate = moment(value).isValid();

      if (value && isValidDate) {
        return moment(value).format('DD MMM YYYY');
      }

      return emptyState;
    }

    case FormQuestionType.TextShort: {
      return <div title={value || ''}>{value ?? emptyState}</div>;
    }

    case FormQuestionType.Number: {
      return value ?? emptyState;
    }

    case FormQuestionType.Attachment: {
      if (value && value.length) {
        return <AttachmentRenderer filesList={value} />;
      }

      return emptyState;
    }

    default: {
      return emptyState;
    }
  }
};

export const CreatedOnRenderer = (params: ICellRendererParams) => {
  if (params.node.group) {
    return null;
  }

  return params.data.createdAt
    ? moment(params.data.createdAt).format('DD MMM YYYY')
    : emptyState;
};

export const UpdatedOnRenderer = (params: ICellRendererParams) => {
  if (params.node.group) {
    return null;
  }

  return params.data.updatedAt
    ? moment(params.data.updatedAt).format('DD MMM YYYY')
    : emptyState;
};

export const dueDateRenderer = (params: ICellRendererParams) => {
  if (params.node.group) {
    return null;
  }

  return params.data.dueDate ? (
    <DueDate
      timestamp={moment(params.data.dueDate)}
      completed={params.data.completedAt != null}
    />
  ) : (
    emptyState
  );
};

export const headerRenderer = (params) => {
  return (
    <HeaderWrapper>
      {params.column.userProvidedColDef.cellRendererParams.questionDeleted ? (
        <>
          <TrashCan16 />
          {params.displayName}
          <Tooltip
            key={params.displayName}
            title={
              <FormattedMessage id="forms.response.deleted_question.tooltip" />
            }
          >
            <HeaderTitleIcon>
              <Information16 />
            </HeaderTitleIcon>
          </Tooltip>
        </>
      ) : (
        <RichTextEditor
          data={params.displayName}
          readonly
          className={styles['form-responses-header']}
        />
      )}
    </HeaderWrapper>
  );
};

export const assigneeRenderer = (params: ICellRendererParams) => {
  if (params.node.group) {
    return null;
  }

  if (!params.data.assignee) {
    return emptyState;
  }

  return (
    <Space>
      {params.data.assignee.map((user) => {
        if (user.assigneeType === 'TEAM') {
          return (
            <UserTag
              color={getRandomColor(user.assignee.teamId)}
              label={user.assignee.teamName}
              icon={
                <PlaceholderAvatar
                  isCustomerRole={
                    user.assignee.teamType === MemberType.External
                  }
                  name={user.assignee.teamName}
                />
              }
            />
          );
        }

        return (
          <UserTag
            color={getRandomColor(user.assignee.userId)}
            imageUrl={user.assignee.photoUrl}
            label={fullName(user.assignee)}
          />
        );
      })}
    </Space>
  );
};
